// app/page.tsx
"use client";

import dynamic from 'next/dynamic';
import Hero from '@/components/Hero';
import Link from 'next/link';
import FAQ from '@/components/FAQ';
import TrustedBy from '@/components/TrustedBy';
import GoogleReviews from '@/components/GoogleReviews';
import {
  FaTruck,
  FaMotorcycle,
  FaCar,
  FaBuilding,
  FaPaw,
  FaGlobeAsia,
  FaBoxes,
  FaShieldAlt,
  FaRupeeSign,
  FaSearch,
  FaUserTie,
  FaClipboardCheck,
  FaThumbsUp,
  FaCalendarAlt,
  FaArrowRight,
  FaStar,
  FaCheckCircle,
  FaPhoneAlt,
} from 'react-icons/fa';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <Hero backgroundImage="/images/movezze_hero_image.webp" />
      <section className="mt-[250px] lg:mt-0">
        <GoogleReviews totalReviews={25000} averageRating={4.9} />
        <FAQ />
        <TrustedBy />
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#292929]">Our Services</h2>
            <p className="text-[#343434] mt-2 max-w-2xl mx-auto">
              Comprehensive relocation solutions tailored to your needs
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={index}
                  href={service.href}
                  className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#f62b1e]/30"
                >
                  <div className="w-12 h-12 bg-[#f62b1e]/10 rounded-xl flex items-center justify-center group-hover:bg-[#f62b1e] transition-colors mb-4">
                    <Icon className="text-[#f62b1e] text-xl group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#292929] mb-2">{service.title}</h3>
                  <p className="text-sm text-[#343434]">{service.description}</p>
                  <div className="mt-4 flex items-center text-sm font-medium text-[#f62b1e] group-hover:text-[#df261a] transition-colors">
                    Learn more <FaArrowRight className="ml-2 text-xs" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#292929]">Why Choose Movezze?</h2>
            <p className="text-[#343434] mt-2 max-w-2xl mx-auto">
              We make moving stress-free with transparency, technology, and trust
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-gray-50 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-[#f62b1e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-[#f62b1e] text-2xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#292929] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#343434]">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#292929]">How It Works</h2>
            <p className="text-[#343434] mt-2">Simple, transparent, and hassle-free</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
                    <div className="w-16 h-16 bg-[#f62b1e] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-white text-2xl" />
                    </div>
                    <div className="absolute -top-3 -right-3 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-bold text-[#343434]">
                      {index + 1}
                    </div>
                    <h3 className="text-xl font-semibold text-[#292929] mb-2">{step.title}</h3>
                    <p className="text-sm text-[#343434]">{step.description}</p>
                  </div>
                  {index < 2 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-[#343434]/30 text-2xl">
                      <FaArrowRight />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#292929]">Cities We Serve</h2>
            <p className="text-[#343434] mt-2">Reliable packers and movers across India</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {cities.map((city, index) => (
              <Link
                key={index}
                href={`/packers-movers-in-${city.toLowerCase()}`}
                className="px-4 py-2 bg-gray-100 hover:bg-[#f62b1e] hover:text-white rounded-full text-sm text-[#343434] transition-colors"
              >
                {city}
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link href="/all-cities" className="text-sm text-[#343434] font-medium hover:text-[#f62b1e] transition-colors">
              View all cities →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#292929] text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Move?</h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Get a free quote today and experience stress-free moving with Movezze.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="/contact"
              className="bg-[#f62b1e] hover:bg-[#df261a] text-white font-semibold px-8 py-3.5 rounded-md transition-colors"
            >
              Get Free Quote
            </Link>
            <Link
              href="/services"
              className="bg-transparent border border-white/30 text-white font-semibold px-8 py-3.5 rounded-md hover:bg-white/10 transition-colors"
            >
              Explore Services
            </Link>
          </div>
          <p className="text-sm text-gray-400 mt-6">
            <FaPhoneAlt className="inline mr-2" /> Call us at +91 8130 880 900
          </p>
        </div>
      </section>
    </div>
  );
}

// ===== DATA =====
const services = [
  {
    title: 'Household Shifting',
    description: 'Safe and insured relocation of all household goods with professional packing.',
    href: '/services/household',
    icon: FaBoxes,
  },
  {
    title: 'Bike Transport',
    description: 'Secure bike transport with protective packaging and door-to-door delivery.',
    href: '/services/bike',
    icon: FaMotorcycle,
  },
  {
    title: 'Car Carrier',
    description: 'Reliable car transportation using premium open or closed carriers.',
    href: '/services/car',
    icon: FaCar,
  },
  {
    title: 'Office Relocation',
    description: 'Complete corporate shifting with minimal downtime and IT equipment handling.',
    href: '/services/office',
    icon: FaBuilding,
  },
  {
    title: 'Pet Relocation',
    description: 'Safe and comfortable pet shifting with specialized handling and care.',
    href: '/services/pet',
    icon: FaPaw,
  },
  {
    title: 'International Relocation',
    description: 'Global moving services with customs clearance and door-to-door delivery.',
    href: '/services/international',
    icon: FaGlobeAsia,
  },
  {
    title: 'Warehouse Service',
    description: 'Secure warehousing and storage solutions for short and long-term needs.',
    href: '/services/warehouse',
    icon: FaClipboardCheck,
  },
  {
    title: 'Goods Insurance',
    description: 'Comprehensive insurance coverage for your belongings during transit.',
    href: '/services/insurance',
    icon: FaShieldAlt,
  },
];

const whyChooseUs = [
  {
    title: '100% Insured',
    description: 'All your belongings are fully insured during transit.',
    icon: FaShieldAlt,
  },
  {
    title: 'No Hidden Charges',
    description: 'Transparent pricing with no surprise fees.',
    icon: FaRupeeSign,
  },
  {
    title: 'GPS Tracking',
    description: 'Real-time tracking of your shipment.',
    icon: FaSearch,
  },
  {
    title: 'Professional Packers',
    description: 'Trained professionals for safe packing.',
    icon: FaUserTie,
  },
];

const howItWorks = [
  {
    title: 'Get a Quote',
    description: 'Fill in your move details and get an instant free estimate.',
    icon: FaCalendarAlt,
  },
  {
    title: 'Book Your Move',
    description: 'Confirm your booking and schedule your move date.',
    icon: FaCheckCircle,
  },
  {
    title: 'Relax & Move',
    description: 'We handle everything – packing, loading, transport, and delivery.',
    icon: FaThumbsUp,
  },
];

const cities = [
  'Delhi', 'Mumbai', 'Bangalore', 'Chennai', 'Hyderabad',
  'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Lucknow',
  'Noida', 'Gurgaon', 'Faridabad', 'Ghaziabad', 'Surat',
  'Nagpur', 'Indore', 'Bhopal', 'Patna', 'Visakhapatnam',
  'Coimbatore', 'Madurai', 'Mysore', 'Chandigarh',
];